/* ============================================
   MessageBubble — Single message in chat
   Renders differently by tipo and direccion
   Supports reply/quote preview + contextual menu
   ============================================ */
import { useState, useRef, useEffect } from 'react'
import type { ReactNode } from 'react'
import AudioPlayer from './AudioPlayer'
import TranscripcionAudio from './TranscripcionAudio'
import { useAuth } from '@/hooks/useAuth'
import IntencionChip from './IntencionChip'
import ChatImage from './ChatImage'
import {
  IconClock, IconCheck, IconCheckCheck, IconEye, IconXCircle,
  IconRefresh, IconDownload, IconAlertTriangle,
  IconMapPin, IconImage as IconImageIcon, IconReply, IconPhone,
} from '@/components/icons/Icons'
import type { Mensaje } from '@/lib/types'
import { fixMojibake } from '@/lib/utils'

interface MessageBubbleProps {
  message: Mensaje
  messageId?: number
  onImageClick: (url: string) => void
  onPdfClick: (url: string, name: string) => void
  onReply: (message: Mensaje) => void
  onForward: (message: Mensaje) => void
  onScrollToMessage?: (id: number) => void
  isGroupStart?: boolean
  isGroupEnd?: boolean
}

function formatTime(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
}

function formatSize(bytes: number | null): string {
  if (!bytes) return ''
  const b = bytes
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / (1024 * 1024)).toFixed(1)} MB`
}

function DeliveryStatus({ estado }: { estado: string }) {
  switch (estado) {
    case 'pendiente':
      return <IconClock size={13} className="chat-bubble__status chat-bubble__status--pendiente" />
    case 'enviado':
      return <IconCheck size={13} className="chat-bubble__status chat-bubble__status--enviado" />
    case 'entregado':
      return <IconCheckCheck size={13} className="chat-bubble__status chat-bubble__status--entregado" />
    case 'leido':
      return <IconCheckCheck size={13} className="chat-bubble__status chat-bubble__status--leido" />
    case 'error':
      return <IconXCircle size={13} className="chat-bubble__status chat-bubble__status--error" />
    default:
      return null
  }
}

function mediaTypeLabel(tipo: string): string {
  switch (tipo) {
    case 'imagen': return 'Imagen'
    case 'audio': return 'Audio'
    case 'video': return 'Video'
    case 'documento': return 'Documento'
    case 'sticker': return 'Sticker'
    case 'ubicacion': return 'Ubicacion'
    default: return tipo
  }
}

function inferFileName(url: string | null): string {
  if (!url) return 'Documento'
  try {
    const parsed = new URL(url)
    // Wazzup stores filename as query param: ?filename=xxx
    const qName = parsed.searchParams.get('filename')
    if (qName) return decodeURIComponent(qName)
    // Fallback: extract from path
    const segments = parsed.pathname.split('/')
    const last = segments[segments.length - 1]
    if (last && last.includes('.')) {
      return decodeURIComponent(last).replace(/^[a-f0-9-]{20,}_/, '')
    }
  } catch { /* ignore */ }
  return 'Documento'
}

interface FileTypeInfo {
  icon: ReactNode
  color: string
  label: string
}

/*
  La hoja se pinta al 30% y las letras ENCIMA iban en blanco. Sobre el chat
  oscuro pasaba; sobre el claro son letras blancas sobre un rosa palido. Van en
  el color pleno, que es lo que hace legible el diminutivo de tres letras.
*/
const FileIcon = ({ letter, tono = 'var(--cat-8)' }: { letter: string; tono?: string }) => (
  <svg width="22" height="26" viewBox="0 0 22 26" fill="none">
    <path d="M2 1h12l6 6v17a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2z" fill={tono} fillOpacity="0.22"/>
    <path d="M14 1v6h6" fill={tono} fillOpacity="0.14"/>
    <text x="11" y="20" textAnchor="middle" fontSize="7" fontWeight="700" fill={tono} fontFamily="system-ui">{letter}</text>
  </svg>
)

function getFileTypeInfo(ext: string, mime: string | null): FileTypeInfo {
  // PDF
  if (ext === 'pdf' || mime?.includes('pdf')) {
    return { icon: <FileIcon letter="PDF" tono="var(--cat-6)" />, color: 'color-mix(in srgb, var(--cat-6) 15%, transparent)', label: 'PDF' }
  }
  // Word
  if (['doc', 'docx'].includes(ext) || mime?.includes('word')) {
    return { icon: <FileIcon letter="DOC" tono="var(--cat-1)" />, color: 'color-mix(in srgb, var(--cat-1) 15%, transparent)', label: ext.toUpperCase() }
  }
  // Excel
  if (['xls', 'xlsx', 'csv'].includes(ext) || mime?.includes('sheet') || mime?.includes('excel')) {
    return { icon: <FileIcon letter="XLS" tono="var(--cat-2)" />, color: 'color-mix(in srgb, var(--cat-2) 15%, transparent)', label: ext.toUpperCase() }
  }
  // PowerPoint
  if (['ppt', 'pptx'].includes(ext) || mime?.includes('presentation')) {
    return { icon: <FileIcon letter="PPT" tono="var(--cat-4)" />, color: 'color-mix(in srgb, var(--cat-4) 15%, transparent)', label: ext.toUpperCase() }
  }
  // Text/Markdown
  if (['txt', 'md', 'rtf'].includes(ext) || mime?.includes('text/plain') || mime?.includes('markdown')) {
    return { icon: <FileIcon letter="TXT" tono="var(--cat-8)" />, color: 'color-mix(in srgb, var(--cat-8) 15%, transparent)', label: ext.toUpperCase() || 'TXT' }
  }
  // Archive
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
    return { icon: <FileIcon letter="ZIP" tono="var(--cat-7)" />, color: 'color-mix(in srgb, var(--cat-7) 15%, transparent)', label: ext.toUpperCase() }
  }
  // Code / JSON
  if (['json', 'html', 'xml', 'js', 'ts', 'css'].includes(ext)) {
    return { icon: <FileIcon letter="COD" tono="var(--cat-3)" />, color: 'color-mix(in srgb, var(--cat-3) 15%, transparent)', label: ext.toUpperCase() }
  }
  // Default
  return { icon: <FileIcon letter="FILE" tono="var(--cat-5)" />, color: 'color-mix(in srgb, var(--cat-5) 15%, transparent)', label: ext ? ext.toUpperCase() : '' }
}

function linkifyText(text: string): ReactNode[] {
  const urlRegex = /(https?:\/\/[^\s<>"')\]]+|www\.[^\s<>"')\]]+|[a-zA-Z0-9][-a-zA-Z0-9]*\.[a-zA-Z]{2,}[^\s<>"')\]]*)/gi
  const parts = text.split(urlRegex)
  return parts.map((part, i) => {
    if (!urlRegex.test(part)) return part
    const href = /^https?:\/\//i.test(part) ? part : `https://${part}`
    return (
      <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="chat-bubble__link">
        {part}
      </a>
    )
  })
}

/*
  El color del nombre sale del NOMBRE, no de la posición del participante en el
  chat: una lista ordenada por llegada le cambia el color a una persona en
  cuanto otra escribe primero, y entonces el color deja de servir para
  reconocerla. Ocho tonos, los mismos `--cat-*` del resto del ERP, que ya tienen
  valor definido en los dos temas.
*/
function colorAutor(nombre: string): string {
  let h = 0
  for (let i = 0; i < nombre.length; i++) h = (h * 31 + nombre.charCodeAt(i)) % 1000003
  return `chat-bubble__autor--c${(h % 8) + 1}`
}

const ForwardIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
)

export default function MessageBubble({ message, messageId, onImageClick, onPdfClick, onReply, onForward, onScrollToMessage, isGroupStart = true, isGroupEnd = true }: MessageBubbleProps) {
  const { isPartner: esPartner } = useAuth()
  const isOut = message.direccion === 'saliente'
  const meta = (message.metadata ?? {}) as Record<string, unknown>
  const mediaFailed = message.media_status === 'download_failed'
  const dirClass = isOut ? 'chat-bubble--saliente' : 'chat-bubble--entrante'

  const alone = isGroupStart && isGroupEnd
  const groupClass = alone
    ? 'chat-bubble--alone'
    : isGroupStart && !isGroupEnd
      ? 'chat-bubble--group-first'
      : !isGroupStart && isGroupEnd
        ? 'chat-bubble--group-last'
        : 'chat-bubble--group-middle'

  const tailClass = isGroupEnd ? 'chat-bubble--tail' : ''
  const groupedClass = !alone ? 'chat-bubble--grouped' : ''

  const [showMenu, setShowMenu] = useState(false)
  const bubbleRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!showMenu) return
    const handleClick = (e: MouseEvent) => {
      if (bubbleRef.current && !bubbleRef.current.contains(e.target as Node)) {
        setShowMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showMenu])

  const renderContent = () => {
    if (mediaFailed && message.tipo !== 'texto') {
      return (
        <div className="chat-bubble__media-warning">
          <IconAlertTriangle size={16} />
          <span>Media no disponible</span>
        </div>
      )
    }

    switch (message.tipo) {
      case 'texto':
        return (
          <p className="chat-bubble__text">
            {linkifyText(fixMojibake(message.contenido || ''))}
          </p>
        )

      case 'imagen':
        return (
          <div className="chat-bubble__image-wrap">
            <ChatImage
              src={message.media_url ?? ''}
              onClick={() => message.media_url && onImageClick(message.media_url)}
            />
            {message.contenido && (
              <p className="chat-bubble__text" style={{ marginTop: 4 }}>{fixMojibake(message.contenido)}</p>
            )}
          </div>
        )

      case 'audio':
        return (
          <div>
            <AudioPlayer
              src={message.media_url ?? ''}
              duration={message.media_duracion ?? undefined}
            />
            <TranscripcionAudio
              mensajeId={message.id}
              texto={message.transcripcion ?? null}
              status={message.transcripcion_status ?? null}
              soloLectura={esPartner}
            />
          </div>
        )

      case 'video':
        return (
          <div>
            <video
              src={message.media_url ?? ''}
              className="chat-bubble__video"
              controls
              preload="metadata"
            >
              Tu navegador no soporta video.
            </video>
            {message.contenido && (
              <p className="chat-bubble__text" style={{ marginTop: 4 }}>{fixMojibake(message.contenido)}</p>
            )}
          </div>
        )

      case 'documento': {
        const isPdf = message.media_mime?.includes('pdf')
        const fileName = message.media_nombre || inferFileName(message.media_url)
        const ext = fileName.split('.').pop()?.toLowerCase() || ''
        const { icon: fileTypeIcon, color: fileTypeColor, label: fileTypeLabel } = getFileTypeInfo(ext, message.media_mime)
        const sizeStr = formatSize(message.media_size)
        const metaLine = [fileTypeLabel, sizeStr].filter(Boolean).join(' · ')

        return (
          <div className="chat-bubble__doc">
            <div className="chat-bubble__doc-body">
              <div className="chat-bubble__doc-icon" style={{ background: fileTypeColor }}>
                {fileTypeIcon}
              </div>
              <div className="chat-bubble__doc-info">
                <span className="chat-bubble__doc-name" title={fileName}>
                  {fileName}
                </span>
                {metaLine && (
                  <span className="chat-bubble__doc-meta">{metaLine}</span>
                )}
              </div>
            </div>
            <div className="chat-bubble__doc-actions">
              {isPdf && (
                <button
                  className="chat-bubble__doc-btn"
                  onClick={() => message.media_url && onPdfClick(
                    message.media_url,
                    fileName
                  )}
                  type="button"
                  title="Ver PDF"
                >
                  <IconEye size={16} />
                </button>
              )}
              <a
                href={message.media_url ?? '#'}
                download={fileName}
                target="_blank"
                rel="noopener noreferrer"
                className="chat-bubble__doc-btn"
                title="Descargar"
              >
                <IconDownload size={16} />
              </a>
            </div>
          </div>
        )
      }

      case 'sticker':
        return (
          <div className="chat-bubble__file" style={{ minWidth: 140 }}>
            <IconImageIcon size={20} />
            <span className="chat-bubble__file-name">Sticker</span>
          </div>
        )

      case 'ubicacion':
        return (
          <div className="chat-bubble__file" style={{ minWidth: 140 }}>
            <IconMapPin size={20} />
            <span className="chat-bubble__file-name">Ubicacion compartida</span>
          </div>
        )

      default:
        return (
          <p className="chat-bubble__text">
            {fixMojibake(message.contenido || '') || '[Mensaje no soportado]'}
          </p>
        )
    }
  }

  return (
    <div id={messageId ? `msg-${messageId}` : undefined} className={`chat-bubble ${dirClass} ${groupClass} ${tailClass} ${groupedClass}`} ref={bubbleRef}>
      {/* Reply preview (quoted message) */}
      {message.reply_preview && (() => {
        const preview = typeof message.reply_preview === 'string'
          ? JSON.parse(message.reply_preview)
          : message.reply_preview
        return (
        <div
          className="chat-bubble__reply-preview"
          onClick={() => message.reply_to_id && onScrollToMessage?.(message.reply_to_id)}
          title="Click para ir al mensaje original"
        >
          <div className="chat-bubble__reply-bar" />
          <div className="chat-bubble__reply-content">
            <span className="chat-bubble__reply-sender">
              {preview.direccion === 'entrante' ? 'Cliente' : 'Tu'}
            </span>
            <span className="chat-bubble__reply-text">
              {preview.tipo !== 'texto'
                ? mediaTypeLabel(preview.tipo)
                : (fixMojibake(preview.contenido?.slice(0, 80) || '') || '...')}
            </span>
          </div>
        </div>
        )
      })()}

      {/* En un grupo cada mensaje lo escribe un participante distinto; sin el autor
          arriba, toda la conversación parece de una sola persona. Cuando Wazzup no
          manda quién escribió (todo lo ENTRANTE de un grupo: su `authorName` viaja
          sólo en los ecos) no se inventa una etiqueta por burbuja — lo dice una vez
          el distintivo "Grupo" de la cabecera. */}
      {meta.grupo === true && message.remitente && (
        <div className={`chat-bubble__autor ${isOut ? '' : colorAutor(message.remitente)}`}>
          {message.remitente}
        </div>
      )}

      {renderContent()}

      <div className="chat-bubble__footer">
        {meta.eliminado === true && <span className="chat-bubble__marca">eliminado</span>}
        {meta.editado === true && <span className="chat-bubble__marca">editado</span>}
        {/* Salió por WhatsApp pero no desde el ERP: lo escribieron en el celular
            o en el chat de Wazzup. Saberlo evita creer que lo mandó el sistema. */}
        {isOut && meta.echo === true && (
          <span
            className="chat-bubble__marca chat-bubble__marca--icono"
            title={`${meta.sent_from_app === true ? 'Enviado desde el chat de Wazzup' : 'Enviado desde el celular'}${message.remitente ? ` por ${message.remitente}` : ''}`}
          >
            <IconPhone size={11} />
          </span>
        )}
        {/* El triador y la transcripción se pagan con NUESTRA cuota de
            Gemini. Mostrárselos al partner sería regalarle consumo sin que
            nadie decida quién lo paga. */}
        {!isOut && !esPartner && (
          <IntencionChip
            intencion={message.intencion ?? null}
            status={message.intencion_status ?? null}
            confianza={message.intencion_confianza ?? null}
          />
        )}
        <span className="chat-bubble__time">{formatTime(message.fecha_mensaje)}</span>
        {isOut && <DeliveryStatus estado={message.estado_entrega} />}
        {isOut && message.estado_entrega === 'error' && (
          <button className="chat-bubble__retry" type="button" title="Reintentar">
            <IconRefresh size={12} />
          </button>
        )}
      </div>

      {/* Contextual action menu (visible on hover, stays open when clicked) */}
      <div className={`chat-bubble__actions ${showMenu ? 'chat-bubble__actions--visible' : ''}`}>
        {!showMenu ? (
          <button
            className="chat-bubble__menu-trigger"
            onClick={() => setShowMenu(true)}
            type="button"
            title="Acciones"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
              <circle cx="12" cy="5" r="2" />
              <circle cx="12" cy="12" r="2" />
              <circle cx="12" cy="19" r="2" />
            </svg>
          </button>
        ) : (
          <div className="chat-bubble__menu">
            <button
              className="chat-bubble__menu-item"
              onClick={() => { onReply(message); setShowMenu(false) }}
              type="button"
            >
              <IconReply size={14} />
              <span>Responder</span>
            </button>
            <button
              className="chat-bubble__menu-item"
              onClick={() => { onForward(message); setShowMenu(false) }}
              type="button"
            >
              <ForwardIcon size={14} />
              <span>Reenviar</span>
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
